/**
 * Central configuration for plan-based quotas
 * All quota limits are defined here for easy maintenance and consistency
 */

export type PlanType = 'free' | 'Starter' | 'Professional' | 'Business' | 'Enterprise';

export interface QuotaLimits {
  projects: number | null; // null = unlimited
  contentLibrarySources: number | null;
  usersPerWorkspace: number | null; // Seat limit per organization plan
  monthlyAISections: number;
  aiGenerationMaxPages: number | null; // Pages one AI generation can create; null = no limit
  customDomain: boolean;
  removeExportBranding: boolean;
  pdfExport: boolean;
  pptxExport: boolean;
}

type QuotaFeatureKey = 'customDomain' | 'removeExportBranding' | 'pdfExport' | 'pptxExport';

/**
 * Quota configuration by plan type
 * Based on plan-quota.md requirements
 */
export const quotaConfig: Record<PlanType, QuotaLimits> = {
  free: {
    projects: 50,
    contentLibrarySources: 3,
    usersPerWorkspace: 1,
    monthlyAISections: 1000,
    aiGenerationMaxPages: 10,
    customDomain: false,
    removeExportBranding: false,
    pdfExport: false,
    pptxExport: false,
  },
  Starter: {
    projects: null,
    contentLibrarySources: 10,
    usersPerWorkspace: 1,
    monthlyAISections: 2000,
    aiGenerationMaxPages: null,
    customDomain: false,
    removeExportBranding: false,
    pdfExport: true,
    pptxExport: false,
  },
  Professional: {
    projects: null,
    contentLibrarySources: 30,
    usersPerWorkspace: 3,
    monthlyAISections: 6000,
    aiGenerationMaxPages: null,
    customDomain: true,
    removeExportBranding: true,
    pdfExport: true,
    pptxExport: true,
  },
  Business: {
    projects: null,
    contentLibrarySources: 100,
    usersPerWorkspace: 10,
    monthlyAISections: 10000,
    aiGenerationMaxPages: null,
    customDomain: true,
    removeExportBranding: true,
    pdfExport: true,
    pptxExport: true,
  },
  Enterprise: {
    projects: null,
    contentLibrarySources: null,
    usersPerWorkspace: null,
    monthlyAISections: Infinity,
    aiGenerationMaxPages: null,
    customDomain: true,
    removeExportBranding: true,
    pdfExport: true,
    pptxExport: true,
  },
};

/**
 * Get quota limits for a given plan
 * @param planName - The plan name (case-insensitive)
 * @returns The quota limits for that plan
 */
export function getQuotaLimits(planName: string | null | undefined): QuotaLimits {
  // Normalize plan name
  const normalizedPlan = normalizePlanName(planName);
  return quotaConfig[normalizedPlan];
}

/**
 * Normalize plan name to match quota config keys
 * @param planName - Raw plan name from database
 * @returns Normalized plan type
 */
export function normalizePlanName(planName: string | null | undefined): PlanType {
  const plan = (planName?.toLowerCase() || 'free') as Lowercase<PlanType>;

  switch (plan) {
    case 'starter':
      return 'Starter';
    case 'professional':
      return 'Professional';
    case 'business':
      return 'Business';
    case 'enterprise':
      return 'Enterprise';
    default:
      return 'free';
  }
}

/**
 * Get quota limit for a specific resource type
 * @param planName - The plan name
 * @param resourceType - The type of resource to check
 * @returns The limit for that resource (null = unlimited)
 */
export function getResourceLimit(
  planName: string | null | undefined,
  resourceType: keyof QuotaLimits,
): number | null | boolean {
  const limits = getQuotaLimits(planName);
  return limits[resourceType];
}

/**
 * Check if a plan allows unlimited access to a resource
 * @param planName - The plan name
 * @param resourceType - The type of resource to check
 * @returns True if unlimited, false if there's a limit
 */
export function isUnlimited(
  planName: string | null | undefined,
  resourceType: keyof Omit<QuotaLimits, QuotaFeatureKey>,
): boolean {
  const limit = getResourceLimit(planName, resourceType);
  return limit === null || limit === Infinity;
}

/**
 * Check if current usage is within quota
 * @param currentUsage - Current usage count
 * @param planName - The plan name
 * @param resourceType - The type of resource to check
 * @returns True if within quota, false if over limit
 */
export function isWithinQuota(
  currentUsage: number,
  planName: string | null | undefined,
  resourceType: keyof Omit<QuotaLimits, QuotaFeatureKey>,
): boolean {
  if (isUnlimited(planName, resourceType)) {
    return true;
  }

  const limit = getResourceLimit(planName, resourceType) as number;
  return currentUsage < limit;
}

/**
 * Get user-friendly error message for quota exceeded
 * @param resourceType - The type of resource that exceeded quota
 * @param planName - Current plan name
 * @returns Error message string
 */
export function getQuotaExceededMessage(
  resourceType: keyof QuotaLimits,
  planName: string | null | undefined,
): string {
  const plan = normalizePlanName(planName);
  const limit = getResourceLimit(planName, resourceType);

  const messages: Record<keyof QuotaLimits, string> = {
    projects: `Project limit reached (${limit} projects on ${plan} plan). Upgrade to create unlimited projects.`,
    contentLibrarySources: `Content source limit reached (${limit} sources on ${plan} plan). Upgrade to add more sources.`,
    usersPerWorkspace: `Seat limit reached for your organization (${limit} ${limit === 1 ? 'seat' : 'seats'} on ${plan} plan). Upgrade to add more members.`,
    monthlyAISections:
      plan === 'free'
        ? `AI credit limit reached (${limit} credits on the free plan). Upgrade to get more credits.`
        : `Monthly AI credit limit reached (${limit} credits on ${plan} plan). Upgrade or wait for next billing cycle.`,
    aiGenerationMaxPages: `AI generation creates up to ${limit} pages on the ${plan} plan. Upgrade to create longer documents.`,
    customDomain: `Custom domains are not available on the ${plan} plan. Please upgrade to add a custom domain.`,
    removeExportBranding: `Proposal.biz branding cannot be removed on the ${plan} plan. Upgrade to Professional to export without branding.`,
    pdfExport: `PDF export is not available on the ${plan} plan. Upgrade to Starter to download PDFs.`,
    pptxExport: `PowerPoint export is not available on the ${plan} plan. Upgrade to Professional to download slides.`,
  };

  return messages[resourceType];
}
